/**
 * `<AppModal>` family — branded in-app prompt + confirm modals.
 *
 * Phase E.4 follow-up (2026-05-20). The first cut of right-click context
 * menus used `window.prompt()` / `window.confirm()` for the rename + delete
 * + new-subfolder + copy-md flows. Those popups are unstyled browser
 * chrome — they break the visual identity and can't be themed.
 *
 * facelift-v3 F5 (2026-05-20): these now compose the generic `<Modal>`
 * primitive (Modal.tsx / Modal.css — the design team's `.modal*` system)
 * instead of the legacy `.inline-token-modal__*` / `.app-modal__*` idiom.
 * The hook APIs (`usePromptModal` / `useConfirmModal`) are unchanged, so call
 * sites keep reading like the legacy window.prompt calls:
 *
 *     const title = await prompt({ title: 'Rename notebook', initial: nb.title });
 *     if (title === null) return; // user cancelled
 *
 * This file defines:
 *
 *   <PromptModal>   — labeled text input + confirm/cancel. Rename / create.
 *   <ConfirmModal>  — message paragraph + confirm/cancel. The primary can be
 *                     `destructive` (red CTA + Cancel-focused) for deletes.
 *
 *   usePromptModal()  → { modal, prompt(opts) }   Promise<string | null>
 *   useConfirmModal() → { modal, confirm(opts) }  Promise<boolean>
 *
 * Behavior (owned by <Modal>): Esc / overlay click / Cancel resolve the
 * Promise; Tab is trapped within the panel; on open the input (prompt) or the
 * focus-appropriate button (confirm) auto-focuses. Enter inside the prompt
 * input confirms.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Modal } from './Modal';

// ---------------------------------------------------------------------------
// <PromptModal> — text-input modal for rename / create flows.
// ---------------------------------------------------------------------------

export interface PromptModalProps {
  /** Header eyebrow above the title. Mono caps; one or two words. */
  eyebrow?: string;
  /** Title rendered as the panel heading. */
  title: string;
  /** Inline placeholder + initial value for the input. */
  initial?: string;
  /** Placeholder when initial is empty. */
  placeholder?: string;
  /** Label for the primary CTA. Defaults to "Save". */
  confirmLabel?: string;
  /** Label for the secondary CTA. Defaults to "Cancel". */
  cancelLabel?: string;
  /** Allow confirming an empty value (for optional fields, e.g. image alt
   *  text, or clearing a link by submitting empty). Default false. */
  allowEmpty?: boolean;
  /** Called with the trimmed input value on confirm. */
  onConfirm: (value: string) => void;
  /** Called on Esc / overlay click / Cancel. */
  onCancel: () => void;
}

export function PromptModal({
  eyebrow,
  title,
  initial = '',
  placeholder = '',
  confirmLabel = 'Save',
  cancelLabel = 'Cancel',
  allowEmpty = false,
  onConfirm,
  onCancel,
}: PromptModalProps): JSX.Element {
  const [value, setValue] = useState(initial);
  const inputRef = useRef<HTMLInputElement>(null);

  // Select-all the seed text on open so the user can type-to-replace (the
  // common case when renaming). <Modal> moves focus to the input via
  // initialFocusRef; this just extends that to a full selection.
  useEffect(() => {
    const t = window.setTimeout(() => inputRef.current?.select(), 0);
    return () => window.clearTimeout(t);
  }, []);

  const trimmed = value.trim();
  const confirmDisabled = !allowEmpty && trimmed.length === 0;

  const submit = (): void => {
    if (confirmDisabled) return;
    onConfirm(trimmed);
  };

  return (
    <Modal
      eyebrow={eyebrow}
      title={title}
      onClose={onCancel}
      initialFocusRef={inputRef}
      footMeta="↵ to save"
      footActions={
        <>
          <button type="button" className="btn" onClick={onCancel}>
            {cancelLabel}
          </button>
          <button
            type="button"
            className="btn btn--cool"
            disabled={confirmDisabled}
            onClick={submit}
          >
            {confirmLabel}
          </button>
        </>
      }
    >
      <div className="modal-form">
        <div className="modal-form__row">
          <input
            ref={inputRef}
            type="text"
            className="modal-form__input"
            value={value}
            placeholder={placeholder}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                submit();
              }
            }}
            aria-label={title}
          />
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// <ConfirmModal> — confirmation modal for delete / destructive flows.
// ---------------------------------------------------------------------------

export interface ConfirmModalProps {
  eyebrow?: string;
  title: string;
  /** The body paragraph. Renders as plain text — no HTML interpolation. */
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** When true, paints the primary CTA red + focuses Cancel by default. */
  destructive?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmModal({
  eyebrow,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  onConfirm,
  onCancel,
}: ConfirmModalProps): JSX.Element {
  const confirmRef = useRef<HTMLButtonElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  // Destructive → default focus to Cancel (safer); benign → primary (faster).
  const initialFocusRef = destructive ? cancelRef : confirmRef;

  return (
    <Modal
      eyebrow={eyebrow}
      title={title}
      onClose={onCancel}
      initialFocusRef={initialFocusRef}
      footActions={
        <>
          <button
            ref={cancelRef}
            type="button"
            className="btn"
            onClick={onCancel}
          >
            {cancelLabel}
          </button>
          <button
            ref={confirmRef}
            type="button"
            className={destructive ? 'btn btn--danger' : 'btn btn--cool'}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </>
      }
    >
      <p className="modal__text">{message}</p>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Promise-returning hooks — drop-in replacements for window.prompt /
// window.confirm with branded UI.
// ---------------------------------------------------------------------------

interface PromptState {
  resolve: (v: string | null) => void;
  opts: Omit<PromptModalProps, 'onConfirm' | 'onCancel'>;
}

/**
 * Promise-returning prompt hook. Call sites:
 *
 *     const { modal, prompt } = usePromptModal();
 *     // ...
 *     const v = await prompt({ title: 'Rename', initial: current });
 *     if (v === null) return; // user cancelled
 *
 * The returned `modal` is `null` when no prompt is active; otherwise it's
 * the `<PromptModal>` element ready to render. Just include it in your
 * component's JSX tree:
 *
 *     return <>{modal}<div>...</div></>;
 */
export function usePromptModal(): {
  modal: JSX.Element | null;
  prompt: (opts: Omit<PromptModalProps, 'onConfirm' | 'onCancel'>) => Promise<string | null>;
} {
  const [state, setState] = useState<PromptState | null>(null);

  const prompt = useCallback(
    (opts: Omit<PromptModalProps, 'onConfirm' | 'onCancel'>) =>
      new Promise<string | null>((resolve) => {
        setState({ resolve, opts });
      }),
    [],
  );

  const modal = state ? (
    <PromptModal
      {...state.opts}
      onConfirm={(v) => {
        state.resolve(v);
        setState(null);
      }}
      onCancel={() => {
        state.resolve(null);
        setState(null);
      }}
    />
  ) : null;

  return { modal, prompt };
}

interface ConfirmState {
  resolve: (v: boolean) => void;
  opts: Omit<ConfirmModalProps, 'onConfirm' | 'onCancel'>;
}

/**
 * Promise-returning confirm hook. Call sites:
 *
 *     const { modal, confirm } = useConfirmModal();
 *     // ...
 *     const ok = await confirm({ title, message, destructive: true });
 *     if (!ok) return; // user cancelled
 */
export function useConfirmModal(): {
  modal: JSX.Element | null;
  confirm: (opts: Omit<ConfirmModalProps, 'onConfirm' | 'onCancel'>) => Promise<boolean>;
} {
  const [state, setState] = useState<ConfirmState | null>(null);

  const confirm = useCallback(
    (opts: Omit<ConfirmModalProps, 'onConfirm' | 'onCancel'>) =>
      new Promise<boolean>((resolve) => {
        setState({ resolve, opts });
      }),
    [],
  );

  const modal = state ? (
    <ConfirmModal
      {...state.opts}
      onConfirm={() => {
        state.resolve(true);
        setState(null);
      }}
      onCancel={() => {
        state.resolve(false);
        setState(null);
      }}
    />
  ) : null;

  return { modal, confirm };
}
